import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import { readContract } from '@wagmi/core';
import Web3 from 'web3';
import { Address } from 'viem';
import BigNumber from 'bignumber.js';

import PriceFeedAbi from '../ContractABI/PriceFeed.json';
import config from '../Config/WagmiConfig';
import {
  REACT_APP_PRICE_FEED_DECIMALS,
  REACT_APP_PRIVATE_SALE,
  REACT_APP_RPC_URL,
  REACT_APP_OZK_VESTING,
  REACT_APP_OZK_VESTING_TOKEN,
  REACT_APP_PRESALE_END_DATE
} from '../Config/Config';
import OZKPRIVATESALEABI from '../ContractABI/OZKPrivateSale.json';
import VestingManagerAbi from '../ContractABI/VestingManager.json';
import VestingWalletCliffAbi from '../ContractABI/VestingWalletCliff.json';
import erc20Abi from '../ContractABI/OZKToken.json';

type DepositContextType = {
  ethPrice: number;
  usdPrice: number;
  startDate: number;
  endDate: number;
  vestedWalletAmount: number;
  balanceOfVestedWallet: number;
  isWhitelisted: boolean;
  isPrivateSaleEnded: boolean;
  isVestedWalletDeployed: boolean;
  vestedWalletAddress: string;
  loadVestedWallet: () => void;
  loadVestedWalletAmount: () => void;
  loadDates: () => void;
  checkWhitelist: (address: string) => Promise<boolean>;
  checkWhitelistWeb3: (address: string) => Promise<boolean>;
};

export const DepositContext = createContext<DepositContextType>({
  ethPrice: 0,
  usdPrice: 0,
  startDate: 0,
  endDate: 0,
  vestedWalletAmount: 0,
  balanceOfVestedWallet: 0,
  isWhitelisted: false,
  isPrivateSaleEnded: false,
  isVestedWalletDeployed: false,
  vestedWalletAddress: '',
  loadVestedWallet: () => undefined,
  loadVestedWalletAmount: () => undefined,
  loadDates: () => undefined,
  checkWhitelist: () => undefined,
  checkWhitelistWeb3: () => undefined,
});

export const DepositContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const web3 = new Web3(REACT_APP_RPC_URL);
  const { isConnected, address } = useAccount();
  const [isWhitelisted, setIsWhitelisted] = useState(true);
  const [isVestedWalletDeployed, setIsVestedWalletDeployed] = useState(false);
  const [vestedWalletAddress, setVestedWalletAddress] = useState('');
  const [vestedWalletAmount, setVestedWalletAmount] = useState(0);
  const [balanceOfVestedWallet, setBalanceOfVestedWallet] = useState(0);
  const [ethPrice, setEthPrice] = useState(0);
  const [usdPrice, setUsdPrice] = useState(0);
  const [priceFeedContract, setPriceFeedContract] = useState('');
  const [startDate, setStartDate] = useState(0);
  const [endDate, setEndDate] = useState(0);

  const checkWhitelist = useCallback(async (address: string) => {
    const result = (await readContract(config, {
      address: REACT_APP_PRIVATE_SALE as Address,
      abi: OZKPRIVATESALEABI,
      functionName: 'checkWhitelisted',
      args: [address],
    })) as boolean;
    return result;
  }, []);

  const checkWhitelistWeb3 = useCallback(async (address: string) => {
    if (!web3.utils.isAddress(address)) {
      return false;
    }
    const PresaleContract = new web3.eth.Contract(
      OZKPRIVATESALEABI,
      REACT_APP_PRIVATE_SALE,
    );
    return (await PresaleContract.methods.checkWhitelisted(address).call()) as boolean;
  }, []);

  const loadDates = useCallback(async () => {
    const PresaleContract = new web3.eth.Contract(
      OZKPRIVATESALEABI,
      REACT_APP_PRIVATE_SALE,
    );
    const startDate = (await PresaleContract.methods.privateSaleStartDate().call()) as number;
    const endDate = parseInt(REACT_APP_PRESALE_END_DATE, 10);
    setStartDate(Number(startDate));
    setEndDate(Number(endDate));
  }, []);

  const isPrivateSaleEnded = useMemo(() => endDate < Math.floor(new Date().getTime() / 1000),[endDate]);

  const loadEthPrice = useCallback(
    async () => {
      if (!isConnected || priceFeedContract === '') {
        return;
      }

      const result = (await readContract(config, {
        address: priceFeedContract as Address,
        abi: PriceFeedAbi,
        functionName: 'latestRoundData',
        args: [],
      }))[1] as number;
      if (result) {
        setEthPrice(
          Number(
            web3.utils.fromWei(result.toString(), REACT_APP_PRICE_FEED_DECIMALS),
          ),
        );
      }
    },
    [isConnected, priceFeedContract],
  );

  const loadUsdPrice = useCallback(async () => {
    if (!isConnected) {
      return;
    }

    const result = (await readContract(config, {
      address: REACT_APP_PRIVATE_SALE as Address,
      abi: OZKPRIVATESALEABI,
      functionName: 'ozkPerUsdQuote',
      args: [],
    })) as number;
    if (result) {
      setUsdPrice(BigNumber(1).div(BigNumber(web3.utils.fromWei(result, 'ether'))).toNumber());
    }
  }, [isConnected]);

  const loadPriceFeedContract = useCallback(async () => {
    if (!isConnected) {
      return;
    }
    const result = (await readContract(config, {
      address: REACT_APP_PRIVATE_SALE as Address,
      abi: OZKPRIVATESALEABI,
      functionName: 'PRICE_FEED',
      args: [],
    })) as string;
    if (result) {
      setPriceFeedContract(result);
    }
  }, [isConnected]);

  const loadVestedWallet = useCallback(async () => {
    if (!isConnected) {
      return;
    }
    const result = (await readContract(config, {
      address: REACT_APP_OZK_VESTING as Address,
      abi: VestingManagerAbi,
      functionName: 'getVestedWallet',
      args: [address],
    })) as [boolean, string];
    if (result) {
      setIsVestedWalletDeployed(result[0]);
      setVestedWalletAddress(result[1]);
    }
  }, [isConnected, address]);

  const loadVestedWalletAmount = useCallback(async () => {
    if (!isConnected || vestedWalletAddress === '' || vestedWalletAddress === '0x0000000000000000000000000000000000000000') {
      return;
    }
    const result = (await readContract(config, {
      address: vestedWalletAddress as Address,
      abi: VestingWalletCliffAbi,
      functionName: 'releasable',
      args: [REACT_APP_OZK_VESTING_TOKEN],
    })) as bigint;

    if (result !== undefined) {
      setVestedWalletAmount(Number(result / BigInt(10 ** 15))/1000);
    }
  }, [isConnected, address, vestedWalletAddress]);

  const loadBalanceOfVestedWallet = useCallback(async () => {
    if (!isConnected || vestedWalletAddress === '' || vestedWalletAddress === '0x0000000000000000000000000000000000000000') {
      return;
    }
    const result = (await readContract(config, {
      address: REACT_APP_OZK_VESTING_TOKEN as Address,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [vestedWalletAddress],
    })) as bigint;

    if (result !== undefined) {
      setBalanceOfVestedWallet(Number(result / BigInt(10 ** 15))/1000);
    }
  }, [isConnected, address, vestedWalletAddress]);

  useEffect(() => {
    loadUsdPrice();
  }, [loadUsdPrice]);

  useEffect(() => {
    loadVestedWallet();
  }, [loadVestedWallet]);

  useEffect(() => {
    loadVestedWalletAmount();
  }, [loadVestedWalletAmount]);

  useEffect(() => {
    loadBalanceOfVestedWallet();
  }, [loadBalanceOfVestedWallet]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  useEffect(() => {
    loadPriceFeedContract();
  }, [loadPriceFeedContract]);

  useEffect(() => {
    loadEthPrice();
  }, [loadEthPrice]);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      loadEthPrice();
    }, 60000);
    return () => clearInterval(timerInterval);
  }, [loadEthPrice]);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      loadVestedWalletAmount();
    }, 10000);
    return () => clearInterval(timerInterval);
  }, [loadVestedWalletAmount]);

  useEffect(() => {
    const check = async () => {
      if (isConnected && address) {
        setIsWhitelisted(await checkWhitelist(address));
      }
    };

    if (isConnected && address) check();
  }, [isConnected, address]);

  useEffect(() => {
    if (!isConnected) {
      setIsVestedWalletDeployed(false);
      setVestedWalletAddress('');
    }
  }, [isConnected]);

  return (
    <DepositContext.Provider
      value={{
        ethPrice,
        usdPrice,
        isWhitelisted,
        checkWhitelist,
        checkWhitelistWeb3,
        loadDates,
        startDate,
        endDate,
        isVestedWalletDeployed,
        vestedWalletAddress,
        loadVestedWallet,
        vestedWalletAmount,
        loadVestedWalletAmount,
        isPrivateSaleEnded,
        balanceOfVestedWallet,
      }}
    >
      {children}
    </DepositContext.Provider>
  );
};

export const useDepositContext = () => {
  return useContext(DepositContext);
};
